import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, type FC } from 'react';

import Debug from '@/components/Debug';
import type { Props } from '@/components/UpcomingAppointmentsList/types';
import { Pill, Typography } from '@/ui';
import { formatTimeOfDayInZone, formatWeekdayDate, getBrowserTimeZone } from '@/utils/dates';
import { groupOccurrencesByWeek } from '@/utils/treatmentGroupOccurrences';

const UpcomingAppointmentsList: FC<Props> = ({ occurrences, timezone }) => {
  const [showAll, setShowAll] = useState(false);
  const zone = timezone || getBrowserTimeZone();
  const weekGroups = groupOccurrencesByWeek(occurrences, zone);

  const visibleWeekGroups = showAll
    ? weekGroups
    : weekGroups.filter((weekGroup) => weekGroup.weeksOut <= 1);
  const hasMore = !showAll && weekGroups.some((weekGroup) => weekGroup.weeksOut > 1);

  return (
    <section>
      <Typography variant="label" muted className="mb-2">
        Upcoming appointments
      </Typography>

      {weekGroups.length === 0 ? (
        <p className="text-sm text-uniform-gray-500">No upcoming sessions scheduled.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-uniform-gray-200 dark:border-uniform-gray-700">
          {visibleWeekGroups.map((weekGroup) => (
            <div key={weekGroup.weeksOut}>
              <div className="flex items-baseline gap-2 border-b border-uniform-gray-200 bg-uniform-gray-50 px-4 py-2 dark:border-uniform-gray-700 dark:bg-uniform-gray-800">
                <Typography variant="body" className="font-semibold">
                  {weekGroup.label}
                </Typography>
                <Typography variant="body" muted className="text-sm">
                  {weekGroup.rangeLabel}
                </Typography>
              </div>

              {weekGroup.occurrences.map((occurrence) => {
                const startAt = occurrence.start_at || occurrence.intended_start_at;
                const endAt = occurrence.end_at || occurrence.intended_end_at;
                return (
                  <div
                    key={occurrence.id}
                    className="relative border-b border-uniform-gray-100 px-4 py-3 last:border-b-0 dark:border-uniform-gray-800">
                    <div className="grid grid-cols-[110px_180px_1fr_auto] items-center gap-4">
                      <Typography variant="body" muted className="text-sm">
                        {formatWeekdayDate(startAt, zone)}
                      </Typography>
                      <Typography variant="body" className="font-semibold">
                        {formatTimeOfDayInZone(startAt, zone)} –{' '}
                        {formatTimeOfDayInZone(endAt, zone)}
                      </Typography>
                      <Typography variant="body" muted>
                        {occurrence.staff_name}
                      </Typography>
                      {!occurrence.appointment_id && (
                        <Pill size="sm" variant="subtle" color="neutral">
                          Not yet booked
                        </Pill>
                      )}
                    </div>
                    <Debug object={occurrence} />
                  </div>
                );
              })}
            </div>
          ))}

          {hasMore && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              className="flex w-full items-center justify-center gap-1 border-t border-uniform-gray-200 py-3 text-sm font-medium text-blue-600 hover:bg-uniform-gray-50 dark:border-uniform-gray-700 dark:text-blue-400 dark:hover:bg-uniform-gray-800">
              See more
              <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </section>
  );
};

export default UpcomingAppointmentsList;
